<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipQualityText, equipTypeList, noPercentEquipAttrKeyList, supremeAffixLevelQualityMap } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import type { SupremeReforgeRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import { cloneDeep } from 'lodash'
import type { UserAttrType } from '@/types/user'
import { getSupremeRealAffixList, supremeAffixCollection } from '@/core/equip/supreme'
import { getSkillNameById } from '@/core/skill'
import { BasicAttrTextEnum } from '@/enums'
import { getReforgePrice } from '../data'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.supremeAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const isUseEquipReforgeMode = ref(false)
const sameNameLevelItemList = computed<EquipItemType[]>(() => {
  if (!equipInfo.value) return []
  const equippedIdList = Object.values(userStore.equippedList)
  return userStore.itemList
    .filter((item) => {
      if (item.id === equipInfo.value?.id) return false
      if (equippedIdList.includes(item.id)) return false
      if (item.isLock) return false
      if (!item.supremeAttrs || !equipInfo.value?.supremeAttrs) return false
      return item.equipId === equipInfo.value.equipId && Number(item.supremeAttrs.level) === Number(equipInfo.value.supremeAttrs.level)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const reforgeAffixOptionList = computed(() => {
  if (!equipInfo.value?.supremeAttrs) return []
  const list = getSupremeRealAffixList(equipInfo.value.supremeAttrs, undefined, equipInfo.value.supremeReforgeAttr)
  return list.slice(1).map((affixKey, index) => ({
    affixKey,
    index: index + 1,
  }))
})

const hadRecord = ref(false)
async function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.supremeAttrs) return ElMessage.warning('仅至臻装备可重铸')

  hadRecord.value = false
  resShow.value = false
  resEquipInfo.value = undefined
  isRecord.value = false
  equipItemId.value = item?.id || ''
  reforgeIndex.value = item?.supremeReforgeAttr?.affixIndex || 1
  isUseEquipReforgeMode.value = false

  if (!item?.id) return

  const record = await socketEmitAsync<boolean>({
    type: 'checkSupremeReforgeRecord',
    data: { itemId: item.id },
  })
  if (equipItemId.value === item.id) {
    hadRecord.value = Boolean(record)
  }
}

function handleEnterUseEquipReforge() {
  if (!equipInfo.value) return ElMessage.warning('请先放入需要重铸的装备')
  if (hadRecord.value) return ElMessage.warning('已存在重铸记录，请先确认重铸结果')
  if (!sameNameLevelItemList.value.length) return ElMessage.warning('暂无可用于重铸的同名同阶级装备')
  isUseEquipReforgeMode.value = true
}

function handleChoiceUseEquipReforgeItem(item: EquipItemType) {
  if (!equipInfo.value) return
  ElMessageBox.confirm(
    `<p class="affix">确定消耗【<span class="info">${item.name}</span>】对当前装备进行<span class="grow">重铸</span>吗？（该操作将<span class="shrink">无法撤销</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await handleReforge(item.id)
      isUseEquipReforgeMode.value = false
    })
    .catch(() => {})
}

const resShow = ref(false)
const resEquipInfo = ref()
const isRecord = ref(false)
const reforgeIndex = ref(1)
async function handleReforge(useEquipItemId?: string) {
  if (!equipInfo.value) return
  if (!hadRecord.value && !useEquipItemId) return ElMessage.warning('请先选择副装备')
  if (useEquipItemId && useEquipItemId === equipInfo.value.id) return ElMessage.warning('主装备和副装备不可相同')
  if (!Number.isInteger(reforgeIndex.value) || reforgeIndex.value <= 0) return ElMessage.warning('重铸位置错误')

  const { id } = equipInfo.value
  const {
    supremeReforgeAttr: resReforgeAttr,
    userAttr,
    isRecord: resIsRecord,
  } = await socketEmitAsync<SupremeReforgeRes>({
    type: 'supremeReforgeEquip',
    data: { mainId: id, subId: useEquipItemId || '', index: reforgeIndex.value },
  })

  if (!resReforgeAttr || !equipInfo.value) return
  isRecord.value = resIsRecord
  if (userAttr) userStore.userAttr = userAttr

  if (!resIsRecord) {
    const useEquipItemIndex = userStore.itemList.findIndex((item) => item.id === useEquipItemId)
    if (useEquipItemIndex >= 0) userStore.itemList.splice(useEquipItemIndex, 1)
    const reforgePrice = equipInfo.value.supremeAttrs ? getReforgePrice(equipInfo.value.supremeAttrs.level) : 0
    userStore.diamond -= reforgePrice
  }

  const item = userStore.itemList.find((item) => item.id === id)
  if (!item || !equipInfo.value) return

  if (!item.supremeReforgeAttr) {
    const curAffixKey = reforgeAffixOptionList.value.find((option) => option.index === reforgeIndex.value)?.affixKey
    if (!curAffixKey) return ElMessage.warning('重铸词条状态异常，请重新选择装备')
    item.supremeReforgeAttr = {
      count: 1,
      affixIndex: reforgeIndex.value,
      affixKey: curAffixKey,
    }
  }
  equipInfo.value.supremeReforgeAttr = cloneDeep(item.supremeReforgeAttr)

  const nextReforgeAttr = {
    ...resReforgeAttr,
    count: resReforgeAttr.count ?? equipInfo.value.supremeReforgeAttr?.count ?? 1,
  }

  resEquipInfo.value = {
    ...cloneDeep(equipInfo.value),
    supremeReforgeAttr: nextReforgeAttr,
  }
  hadRecord.value = true

  reforgeIndex.value = nextReforgeAttr.affixIndex
  resShow.value = true
}
async function handleChoiceReforgeRes(type: 0 | 1) {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">${type === 0 ? '保留' : '覆盖'}</span>重铸吗？（该操作将导致选中的装备<span class="shrink">销毁</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!equipInfo.value || !resEquipInfo.value) return

      const itemId = equipInfo.value.id

      const userAttr = await socketEmitAsync<UserAttrType>({ type: 'choiceSupremeReforgeRes', data: { itemId, choiceIndex: type } })
      const item = userStore.itemList.find((item) => item.id === itemId)
      if (!item || !item.supremeReforgeAttr || !equipInfo.value.supremeReforgeAttr || !resEquipInfo.value?.supremeReforgeAttr) return
      if (userAttr) userStore.userAttr = userAttr

      const { count } = resEquipInfo.value.supremeReforgeAttr
      if (type === 1) {
        const resReforgeAttr = resEquipInfo.value.supremeReforgeAttr
        item.supremeReforgeAttr = resReforgeAttr
        equipInfo.value.supremeReforgeAttr = resReforgeAttr
      } else {
        item.supremeReforgeAttr.count = count
        equipInfo.value.supremeReforgeAttr.count = count
      }

      resShow.value = false
      resEquipInfo.value = undefined
      hadRecord.value = false
      isRecord.value = false
      ElMessage.success('重铸确认成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>重铸说明</h4>
            <p>1. 通过<span class="info">重铸</span>可重新随机至臻属性词缀。</p>
            <p>2. 可选择最多一个词缀进行重铸，重铸后无法选择另一个。</p>
            <p>3. 重铸产出的词缀品质及概率受装备品级影响。</p>
            <p>4. 重铸需消耗<span class="info">同名同阶级</span>装备。</p>
            <p>5. 每次重铸后都可自主选择保留/覆盖重铸词缀，若您忘记选择，可再次选取该装备继续确认。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo?.supremeAttrs">
        <div class="info-card">
          <p style="display: flex; align-items: center">
            <span>重铸次数：{{ equipInfo.supremeReforgeAttr?.count || 0 }}</span>
          </p>
          <p>重铸花费：{{ hadRecord ? 0 : getReforgePrice(equipInfo.supremeAttrs.level) }} 钻石</p>
          <template v-if="equipInfo.supremeAttrs">
            <p style="justify-content: flex-start; align-items: center">
              <span>选择重铸词缀：</span>
              <el-radio-group v-model="reforgeIndex" :disabled="Boolean(equipInfo.supremeReforgeAttr)">
                <el-radio v-for="option of reforgeAffixOptionList" :value="option.index">
                  <span :class="supremeAffixLevelQualityMap[supremeAffixCollection[option.affixKey].level]">
                    {{ supremeAffixCollection[option.affixKey].name }}
                  </span>
                </el-radio>
              </el-radio-group>
            </p>
          </template>
        </div>
        <div class="btn-wrap reforge-btn-wrap">
          <el-button
            v-if="!isUseEquipReforgeMode"
            type="warning"
            plain
            @click="hadRecord ? handleReforge() : handleEnterUseEquipReforge()"
            style="margin-left: 0"
          >
            {{ hadRecord ? '确认结果' : '开始重铸' }}
          </el-button>
          <el-button v-if="isUseEquipReforgeMode" type="danger" plain @click="isUseEquipReforgeMode = false" style="margin-left: 0"> 取消 </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!isUseEquipReforgeMode">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package" v-show="!isUseEquipReforgeMode">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package" v-show="isUseEquipReforgeMode">
      <h4>选择同名同阶级装备作为材料</h4>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of sameNameLevelItemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoiceUseEquipReforgeItem(item)">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
        <p v-if="!sameNameLevelItemList.length" style="text-align: center">暂无可用于重铸的同名同阶级装备</p>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px" :before-close="() => false">
      <h2 class="affix">
        <span class="grow">{{ isRecord ? '上次重铸结果未确认' : '重铸成功' }}</span>
      </h2>
      <p style="text-align: center" v-show="resEquipInfo">请选择保留的词缀</p>
      <div class="reforge-res">
        <div class="left border-wrap" v-if="equipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="equipInfo.supremeReforgeAttr?.affixKey">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[equipInfo.supremeReforgeAttr.affixKey].level]">
              {{ supremeAffixCollection[equipInfo.supremeReforgeAttr.affixKey].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[equipInfo.supremeReforgeAttr.affixKey].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap" v-show="resEquipInfo">
            <el-button type="primary" plain size="small" @click="handleChoiceReforgeRes(0)">保留</el-button>
          </div>
        </div>
        <div class="right border-wrap" v-if="resEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, resEquipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="resEquipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="resEquipInfo.supremeReforgeAttr">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[resEquipInfo.supremeReforgeAttr.affixKey].level]">
              {{ supremeAffixCollection[resEquipInfo.supremeReforgeAttr.affixKey].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[resEquipInfo.supremeReforgeAttr.affixKey].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap">
            <el-button type="warning" plain size="small" @click="handleChoiceReforgeRes(1)">覆盖</el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center" v-show="!resEquipInfo">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .reforge-btn-wrap {
    button {
      margin-left: 0;
      margin-bottom: 10px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .reforge-res {
    display: flex;
    justify-content: space-between;
    > div {
      width: calc(50% - 5px);
      margin: 0 auto;
      flex: 1;
    }
    .border-wrap {
      padding: 10px;
      p {
        margin: 4px 0;
      }
    }
  }
}
</style>
